/**
 * VerETPPage — visualização do ETP gerado (F6a).
 *
 * Réplica do view_etp.html: cabeçalho, ações, cards da cadeia, sumário das
 * 13 seções do Art. 18 §1º + o Anexo I (mapa de risco), o card de
 * modalidade — que vem do DFD — e as subseções que a IA desenvolveu.
 */

import {
  faArrowLeft,
  faBalanceScale,
  faCalendar,
  faCheck,
  faCheckCircle,
  faChevronRight,
  faEdit,
  faExclamationTriangle,
  faEye,
  faFileAlt,
  faFileContract,
  faFileInvoice,
  faFileWord,
  faGavel,
  faInfoCircle,
  faListOl,
  faPlus,
  faScroll,
  faSpinner,
  faTrashAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { documentoETP, excluirETP, type DocumentoETP } from '../../api/etp'
import { useToast } from '../../components/Toast'

const CHAVE_ANEXO = 'mapa_risco'

function CardCadeia({
  titulo, descricao, icone, gradiente, estado, verUrl, editarUrl, criarUrl,
}: {
  titulo: string
  descricao: string
  icone: IconDefinition
  gradiente: string
  estado: 'atual' | 'pronto' | 'pendente'
  verUrl?: string
  editarUrl?: string
  criarUrl?: string
}) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-slate-700 dark:bg-dark-card">
      <div className="mb-4 flex items-start justify-between">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg transition-transform duration-300 group-hover:scale-110 ${gradiente}`}>
          <FontAwesomeIcon icon={icone} className="text-lg text-white" />
        </span>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
            estado === 'atual'
              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300'
              : estado === 'pronto'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
          }`}
        >
          {estado !== 'pendente' && <FontAwesomeIcon icon={faCheck} className="mr-1" />}
          {estado === 'atual' ? 'Visualizando' : estado === 'pronto' ? 'Criado' : 'Pendente'}
        </span>
      </div>
      <h3 className="mb-1 text-lg font-bold text-slate-800 dark:text-white">{titulo}</h3>
      <p className="mb-5 min-h-[2.5em] text-xs text-slate-500 dark:text-slate-400">{descricao}</p>
      <div className="flex gap-2">
        {verUrl && (
          <a href={verUrl} className="flex flex-1 items-center justify-center rounded-lg border border-indigo-200 bg-white py-2 text-sm font-semibold text-indigo-700 shadow-sm transition-all hover:bg-indigo-600 hover:text-white dark:border-indigo-800 dark:bg-slate-700 dark:text-indigo-400">
            <FontAwesomeIcon icon={faEye} className="mr-2" />
            Ver
          </a>
        )}
        {editarUrl && (
          <a href={editarUrl} aria-label={`Editar ${titulo}`} className="flex w-10 items-center justify-center rounded-lg border border-emerald-200 bg-white py-2 text-emerald-700 shadow-sm transition-all hover:bg-emerald-600 hover:text-white dark:border-emerald-800 dark:bg-slate-700 dark:text-emerald-400">
            <FontAwesomeIcon icon={faEdit} />
          </a>
        )}
        {criarUrl && (
          <a href={criarUrl} className="flex flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white py-2 text-sm font-semibold text-slate-600 shadow-sm transition-all hover:bg-slate-600 hover:text-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Criar
          </a>
        )}
      </div>
    </div>
  )
}

function CardModalidade({ etp }: { etp: DocumentoETP }) {
  const m = etp.modalidade
  if (!m) return null
  const direta = m.tipo_contratacao_direta !== 'licitacao'
  return (
    <div className="mb-6 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
      <h3 className="mb-4 text-lg font-semibold text-indigo-800 dark:text-indigo-300">
        <FontAwesomeIcon icon={faGavel} className="mr-2" />
        {direta ? 'Contratação Direta (Lei 14.133/2021)' : 'Modalidade de Licitação (Art. 28, Lei 14.133/2021)'}
        <span className="ml-2 text-xs font-normal text-indigo-600 dark:text-indigo-400">herdada do DFD</span>
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-indigo-100 bg-white p-4 dark:border-indigo-900 dark:bg-dark-card">
          <p className="mb-1 text-sm font-medium text-slate-500">Tipo de Objeto</p>
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">{m.tipo_objeto_rotulo}</p>
        </div>
        <div className="rounded-lg border border-purple-100 bg-white p-4 dark:border-purple-900 dark:bg-dark-card">
          <p className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-500">
            <FontAwesomeIcon icon={faBalanceScale} />
            {direta ? 'Fundamento' : 'Modalidade'}
          </p>
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">{m.modalidade_rotulo}</p>
          {!direta && (
            <span className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs ${m.obrigatoria ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}>
              {m.obrigatoria ? 'Modalidade Obrigatória' : 'Modalidade Alternativa'}
            </span>
          )}
        </div>
        <div className="rounded-lg border border-emerald-100 bg-white p-4 dark:border-emerald-900 dark:bg-dark-card">
          <p className="mb-1 text-sm font-medium text-slate-500">Regime</p>
          {m.registro_precos ? (
            <>
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-sm text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                <FontAwesomeIcon icon={faFileInvoice} className="mr-1" />
                SRP/ARP
              </span>
              <p className="mt-1 text-xs text-slate-500">Sistema de Registro de Preços (Art. 82-86)</p>
            </>
          ) : (
            <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">Contratação Direta</p>
          )}
        </div>
      </div>
      {m.justificativa && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <h4 className="mb-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Justificativa da Modalidade
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-400">{m.justificativa}</p>
        </div>
      )}
    </div>
  )
}

function ModalExcluir({ etp, aoFechar }: { etp: DocumentoETP; aoFechar: () => void }) {
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const excluir = useMutation({
    mutationFn: () => excluirETP(etp.id),
    onSuccess: (dados) => {
      void clienteQuery.invalidateQueries({ queryKey: ['processos-licitatorios'] })
      notificar('success', dados.tinha_tr
        ? 'ETP excluído. Atenção: o TR foi gerado a partir dele.'
        : 'ETP excluído. O processo e o DFD foram mantidos.')
      navegar('/processos')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-label="Excluir ETP"
      onMouseDown={(e) => { if (e.target === e.currentTarget) aoFechar() }}
    >
      <div className="mx-4 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-2xl dark:border-slate-700 dark:bg-dark-card">
        <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-600 shadow-lg shadow-red-500/30">
          <FontAwesomeIcon icon={faTrashAlt} className="text-2xl text-white" />
        </span>
        <h3 className="mb-2 text-xl font-bold text-slate-800 dark:text-slate-100">Excluir ETP?</h3>
        <p className="mb-4 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800 dark:bg-slate-700 dark:text-slate-200">{etp.objeto}</p>
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-left dark:bg-red-900/20">
          <p className="mb-2 text-sm font-medium text-red-700 dark:text-red-400">Esta ação irá:</p>
          <ul className="list-inside list-disc space-y-1 text-sm text-red-600 dark:text-red-400">
            <li>Remover as seções do ETP</li>
            <li>Manter o processo e o DFD</li>
            {etp.cadeia.tem_tr && (
              <li className="font-semibold">O TR já gerado passa a citar um ETP que não existe mais</li>
            )}
          </ul>
        </div>
        <p className="mb-6 text-sm font-semibold text-red-600 dark:text-red-400">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          Esta ação não pode ser desfeita!
        </p>
        <div className="flex justify-center gap-3">
          <button type="button" onClick={aoFechar} className="rounded-xl bg-slate-200 px-6 py-3 font-medium text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300">
            Cancelar
          </button>
          <button type="button" onClick={() => excluir.mutate()} disabled={excluir.isPending} className="rounded-xl bg-gradient-to-r from-red-500 to-orange-600 px-6 py-3 font-semibold text-white shadow-lg shadow-red-500/30 transition-all hover:from-red-600 hover:to-orange-700 disabled:opacity-60">
            <FontAwesomeIcon icon={excluir.isPending ? faSpinner : faTrashAlt} spin={excluir.isPending} className="mr-2" />
            Sim, Excluir
          </button>
        </div>
      </div>
    </div>
  )
}

export function VerETPPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const [excluindo, setExcluindo] = useState(false)

  const { data: etp, isLoading, error } = useQuery({
    queryKey: ['etp-documento', idNumerico],
    queryFn: () => documentoETP(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando ETP…
      </p>
    )
  }
  if (error || !etp) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faFileContract} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">ETP não encontrado</h1>
        <p className="mb-6 text-slate-500">{(error as Error)?.message ?? 'O documento não existe ou não é seu.'}</p>
        <Link to="/processos" className="inline-flex items-center rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  const { completude } = etp

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-4xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faFileContract} className="mr-3 text-indigo-600" />
          Estudo Técnico Preliminar
        </h1>
        <p className="mb-4 text-xl text-slate-600 dark:text-slate-400">{etp.objeto}</p>
        <div className="flex flex-wrap justify-center gap-4">
          {etp.criado_em && (
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
              <FontAwesomeIcon icon={faCalendar} className="mr-2" />
              Criado em: {etp.criado_em}
            </span>
          )}
          <span className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-medium ${completude.indispensaveis >= completude.total_indispensaveis ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}>
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            {completude.indispensaveis}/{completude.total_indispensaveis} elementos indispensáveis
          </span>
          <span className="inline-flex items-center rounded-full bg-purple-100 px-4 py-2 text-sm font-medium text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
            <FontAwesomeIcon icon={faGavel} className="mr-2" />
            Lei 14.133/2021 - Art. 18
          </span>
        </div>
      </header>

      <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
        <Link to={`/etp/${etp.id}/editar`} className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-blue-700">
          <FontAwesomeIcon icon={faEdit} className="mr-2" />
          Editar
        </Link>
        <a href={`/etp/${etp.id}/export/docx`} className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-emerald-700">
          <FontAwesomeIcon icon={faFileWord} className="mr-2" />
          Exportar DOCX
        </a>
        <button type="button" onClick={() => setExcluindo(true)} className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-red-700">
          <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
          Excluir
        </button>
        <Link to="/processos" className="inline-flex items-center justify-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <CardCadeia titulo="DFD" descricao="Documento de Formalização da Demanda" icone={faFileAlt}
          gradiente="from-green-500 to-emerald-600"
          estado={etp.cadeia.tem_dfd ? 'pronto' : 'pendente'}
          verUrl={etp.cadeia.tem_dfd ? `/app/dfd/${etp.id}` : undefined}
          editarUrl={etp.cadeia.tem_dfd ? `/app/dfd/${etp.id}/editar` : undefined} />
        <CardCadeia titulo="ETP" descricao="Estudo Técnico Preliminar" icone={faFileContract}
          gradiente="from-indigo-500 to-purple-600" estado="atual"
          editarUrl={`/app/etp/${etp.id}/editar`} />
        <CardCadeia titulo="TR" descricao="Termo de Referência" icone={faFileContract}
          gradiente="from-blue-500 to-cyan-600"
          estado={etp.cadeia.tem_tr ? 'pronto' : 'pendente'}
          verUrl={etp.cadeia.tem_tr ? `/app/tr/${etp.id}` : undefined}
          criarUrl={etp.cadeia.tem_tr ? undefined : `/app/tr/novo/${etp.id}`} />
        <CardCadeia titulo="Edital" descricao="Edital de Licitação" icone={faScroll}
          gradiente="from-purple-500 to-violet-600"
          estado={etp.cadeia.tem_edital ? 'pronto' : 'pendente'}
          verUrl={etp.cadeia.tem_edital ? `/app/edital/${etp.id}` : undefined}
          criarUrl={!etp.cadeia.tem_edital && etp.cadeia.tem_tr ? `/app/edital/novo/${etp.id}` : undefined} />
      </div>

      <nav aria-label="Sumário" className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-white">
          <FontAwesomeIcon icon={faListOl} className="mr-2 text-indigo-600" />
          Sumário
        </h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-5">
          {etp.secoes.map((secao) => (
            <a key={secao.chave} href={`#secao-${secao.chave}`}
              className={`block rounded-lg px-3 py-2 text-sm transition-colors ${secao.chave === CHAVE_ANEXO ? 'bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400' : 'text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 dark:text-slate-400 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400'}`}>
              {secao.titulo.split(' - ')[0]}
            </a>
          ))}
        </div>
      </nav>

      <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
          <h2 className="flex items-center text-2xl font-bold text-white">
            <FontAwesomeIcon icon={faFileContract} className="mr-3" />
            Estudo Técnico Preliminar (ETP)
          </h2>
          <p className="mt-1 text-indigo-100">
            Lei 14.133/2021 - Art. 18 | {completude.preenchidas}/{completude.total} seções preenchidas
          </p>
        </div>

        <div className="space-y-6 p-8">
          <CardModalidade etp={etp} />

          {etp.secoes.map((secao) => {
            const anexo = secao.chave === CHAVE_ANEXO
            return (
              <section key={secao.chave} id={`secao-${secao.chave}`}
                className={`overflow-hidden rounded-lg border transition-shadow hover:shadow-md ${anexo ? 'border-orange-200 bg-gradient-to-r from-orange-50 to-red-50 dark:border-orange-800 dark:from-orange-900/20 dark:to-red-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                <h3 className={`flex items-center border-b px-6 py-4 text-lg font-semibold text-slate-800 dark:text-slate-100 ${anexo ? 'border-orange-200 bg-gradient-to-r from-orange-100 to-red-100 dark:border-orange-800 dark:from-orange-900/40 dark:to-red-900/40' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800'}`}>
                  <FontAwesomeIcon icon={anexo ? faExclamationTriangle : faChevronRight}
                    className={`mr-2 text-sm ${anexo ? 'text-orange-500' : 'text-indigo-500'}`} />
                  {secao.titulo}
                  {anexo && (
                    <span className="ml-2 rounded-full bg-orange-200 px-2 py-1 text-xs font-medium text-orange-800">MATRIZ P×I</span>
                  )}
                </h3>
                <div className="p-6">
                  {secao.html ? (
                    <div className="conteudo-documento leading-relaxed text-slate-700 dark:text-slate-300"
                      dangerouslySetInnerHTML={{ __html: secao.html }} />
                  ) : (
                    <div className="py-8 text-center text-slate-500">
                      <FontAwesomeIcon icon={faEdit} className="mb-3 text-3xl" />
                      <p>Esta seção ainda não foi preenchida.</p>
                      <Link to={`/etp/${etp.id}/editar`} className="mt-3 inline-flex items-center text-indigo-600 hover:text-indigo-800">
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        Preencher agora
                      </Link>
                    </div>
                  )}
                </div>
              </section>
            )
          })}

          {etp.subsecoes.length > 0 && (
            <section className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
              <h3 className="border-b border-slate-200 bg-slate-50 px-6 py-4 text-lg font-semibold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-sm text-indigo-500" />
                Subseções informadas
              </h3>
              <div className="space-y-4 p-6">
                {etp.subsecoes.map((sub) => (
                  <div key={sub.chave}>
                    <p className="subsection-title">{sub.titulo}</p>
                    <div className="conteudo-documento text-slate-700 dark:text-slate-300"
                      dangerouslySetInnerHTML={{ __html: sub.html }} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {etp.referencias.length > 0 && (
            <section className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
              <h3 className="border-b border-slate-200 bg-slate-50 px-6 py-4 text-lg font-semibold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                <FontAwesomeIcon icon={faGavel} className="mr-2 text-sm text-indigo-500" />
                Fundamentação Legal
              </h3>
              <ul className="space-y-2 p-6 text-sm text-slate-700 dark:text-slate-300">
                {etp.referencias.map((ref, i) => <li key={i}>• {ref}</li>)}
              </ul>
            </section>
          )}
        </div>
      </article>

      {excluindo && <ModalExcluir etp={etp} aoFechar={() => setExcluindo(false)} />}
    </div>
  )
}
